import { type FC, useState } from "react";
import { ADT } from "pb.adt";
import { styled } from "@linaria/react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { t } from "@/themes/theme.ts";
import { Switch } from "@/components/Switch.tsx";
import { AiStates } from "@/types/ai.ts";
import { postFlowAiAutoSend } from "@/api/admin.ts";
import { HttpError, isThrottled409 } from "@/api/shared.ts";
import { type HeldReplyState, useChatStore } from "@/stores/useChatStore.ts";
import { IconPlus } from "@/icons/IconPlus.tsx";
import { ButtonIcon } from "@/components/buttons/ButtonIcon.tsx";
import { toastAndError } from "@/helpers/toast.ts";

interface ChatAiAutoSendSwitchProps {
  flowGeneratorSessionId: number;
  // Server truth: true = the AI tutor owns the session (auto-send active),
  // false = paused/queued
  aiOwned: boolean;
  // Review state of the session's pending reply; an unresolved hold locks the
  // switch off (the backend also refuses with a 409 — that stays as the race
  // backstop, not the primary UX)
  heldState?: HeldReplyState;
}

const SwitchColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  min-width: 0;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &[data-disabled="true"] {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Label = styled.span`
  font-size: var(--13px);
  user-select: none;
  white-space: nowrap;
`;

// The backend's own explanation of why the toggle was refused (409 — e.g. an
// unresolved hold), rendered right next to the switch rather than as a toast
const ConflictNote = styled.span`
  font-size: var(--13px);
  text-align: right;
  overflow-wrap: anywhere;
  opacity: 0.9;
`;

const BarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
`;

export const ChatAiAutoSendSwitch: FC<ChatAiAutoSendSwitchProps> = ({
  flowGeneratorSessionId,
  aiOwned,
  heldState,
}) => {
  const { SoftResetChat } = useChatStore();
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  const toggleAutoSend = useMutation({
    mutationFn: (enabled: boolean) => postFlowAiAutoSend(flowGeneratorSessionId, enabled),
    onMutate: () => {
      setConflictMessage(null);
    },
    onSuccess: (_, enabled) => {
      SoftResetChat(flowGeneratorSessionId);
      toast.success(
        enabled
          ? "AI auto-send re-enabled — the AI tutor has re-taken this session"
          : "AI paused — the session has been released into the tutor queue",
      );
    },
    onError: (e, enabled) => {
      // A business 409 (any unresolved hold — student message or AI reply —
      // refuses the toggle) carries an explanatory body: pin it next to the
      // switch and refresh so the hold surfaces in the review panel
      if (e instanceof HttpError && e.statusCode === 409 && !isThrottled409(e)) {
        setConflictMessage(e.bodyText);
        return;
      }

      toastAndError(
        enabled ? "Could not re-enable AI auto-send" : "Could not pause the AI",
        "This session no longer has an open intervention — refreshing",
        e,
      );
    },
  });

  const heldUnresolved = !!heldState && ADT.match(heldState, "held");

  // Optimistic while in flight, sticky after success (until the refetches
  // triggered in onSuccess re-gate the parent); an error resets the mutation
  // state so the display falls back to server truth automatically
  const displayed =
    toggleAutoSend.isPending || toggleAutoSend.isSuccess
      ? (toggleAutoSend.variables ?? aiOwned)
      : aiOwned;

  const tooltip = heldUnresolved
    ? "A flagged message is awaiting review — approve it or claim the session first"
    : displayed
      ? "AI auto-send is on — turning it off pauses the AI and releases this chat into the tutor queue"
      : "Re-enable AI auto-send — the AI tutor re-takes this session and it leaves the queue";

  return (
    <SwitchColumn>
      <Wrapper data-disabled={heldUnresolved} data-tooltip={tooltip} data-direction={"top"}>
        <Label>AI auto-send</Label>
        <Switch
          state={displayed}
          aiMode={displayed ? AiStates.on : heldUnresolved ? AiStates.paused : AiStates.off}
          callback={(newState) => {
            if (heldUnresolved || toggleAutoSend.isPending) return;
            toggleAutoSend.mutate(newState);
          }}
        />
      </Wrapper>
      {conflictMessage && <ConflictNote>{conflictMessage}</ConflictNote>}
    </SwitchColumn>
  );
};

const Bar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 0.5rem ${t.chatPaddingSides};
  background-color: ${t.chatFooterAiBackground};
  color: white;
`;

export const ChatAiAutoSendBar: FC<{
  flowGeneratorSessionId: number;
  aiOwned: boolean;
  heldState?: HeldReplyState;
  isInReview: boolean;
  // Post-approve limbo on a review assignment: the hold is resolved but the
  // session still reads as the reviewer's. Offers a claim action and adjusts
  // the copy — resuming auto-send (the switch) is the "hand back to AI" path.
  onClaim: () => void;
}> = ({ flowGeneratorSessionId, aiOwned, heldState, onClaim, isInReview }) => {
  // An unresolved hold means the AI is paused server-side even while the
  // session's ownership field still reads as AI-owned
  const heldUnresolved = !!heldState && ADT.match(heldState, "held");
  const paused = !aiOwned || heldUnresolved;

  return (
    <Bar>
      <span>
        {isInReview
          ? "In review — the AI still runs this chat, paused until the flagged message is approved (or the chat is claimed)"
          : "Eedi AI Tutor is handling this session"}
      </span>
      <BarActions>
        {/* Post-approve limbo is the ONLY state where this bar is the sole
            exit from the session (footer/header/input are all withheld), so
            the claim path the copy promises has to be a real button here */}
        {isInReview && (
          <ButtonIcon
            icon={<IconPlus color={t.brandNeutralWhite} width={"14"} height={"14"} />}
            text={"Claim session"}
            onClick={(unlock) => {
              onClaim();
              unlock();
            }}
            backgroundColor={t.brandAccentBlue}
            flare
          />
        )}
        <ChatAiAutoSendSwitch
          flowGeneratorSessionId={flowGeneratorSessionId}
          aiOwned={!paused}
          heldState={heldState}
        />
      </BarActions>
    </Bar>
  );
};
